import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo } from 'react';

import { GiveBoxEvent } from '@bonusly/common-give-box';
import { rem, VStack } from '@bonusly/design-system';
import { useEventListener } from '@bonusly/events';
import { Permission, ProtectedContent, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { DirectReport } from '../../../../types/user';
import { PersonList } from '../../../bonus-feed/features/recognition/components/tracker-widget/components/person-list';
import { ProgressBar } from '../../../bonus-feed/features/recognition/components/tracker-widget/components/progress-bar';
import { QueryKey } from '../../../bonus-feed/features/recognition/components/tracker-widget/hooks';
import { Person } from '../../../bonus-feed/features/recognition/components/tracker-widget/types';
import { useUserMeetingsV2 } from '../../../growth/components/meeting-v2/hooks/use-user-meetings-v2';
import { DirectReportList, EnrichedDirectReport } from './direct-report-list';

interface TeamTrackerProps {
	directReports: DirectReport[];
	people?: Person[];
}

export const TeamTracker: FC<TeamTrackerProps> = ({ directReports, people }) => {
	const queryClient = useQueryClient();
	const currentUser = useCurrentUser();
	const hasDirectReports = directReports.length > 0;
	const canAccessOneOnOnes = usePermission(Permission.CanAccessOneOnOnes);
	const { data: meetings } = useUserMeetingsV2({ enabled: hasDirectReports && canAccessOneOnOnes });

	// Build a set of user IDs that have an active (upcoming) 1:1 with the current user
	const userIdsWithMeetings = useMemo(() => {
		const ids = new Set<string>();
		if (!meetings) return ids;
		for (const meeting of meetings) {
			if (meeting.isUpcoming()) {
				for (const participantId of meeting.participantIds) {
					if (participantId !== currentUser.id) {
						ids.add(participantId);
					}
				}
			}
		}
		return ids;
	}, [meetings, currentUser.id]);

	// Invalidate team tracker query when recognition is given/deleted
	// This ensures the recognition count and last recognized at update automatically
	useEventListener(GiveBoxEvent.Created, () => {
		queryClient.invalidateQueries([QueryKey.TeamTracker]);
	});

	useEventListener(GiveBoxEvent.Deleted, () => {
		queryClient.invalidateQueries([QueryKey.TeamTracker]);
	});

	// NOTE: need to add recognition count and last recognized at to the direct reports
	// using the team tracker data. When people data is not yet loaded, fall back to
	// directReports data to avoid showing an empty list while loading.
	// Also override hasUpcomingOneOnOne using the meetings query, which is more reliable
	// than the backend field from the direct reports API.
	const updatedDirectReports: EnrichedDirectReport[] = useMemo(() => {
		if (!people) {
			return directReports.map((report) => ({
				...report,
				hasUpcomingOneOnOne: userIdsWithMeetings.has(report.id),
				lastRecognizedAt: null,
				recognitionCount: report.recognitionCount,
			}));
		}

		const peopleById = new Map(people.map((person) => [person.id, person]));

		return directReports.map((report) => {
			const person = peopleById.get(report.id);
			return {
				...report,
				hasUpcomingOneOnOne: userIdsWithMeetings.has(report.id),
				lastRecognizedAt: person?.last_recognized_at ?? null,
				recognitionCount: person?.recognition_count ?? report.recognitionCount,
			};
		});
	}, [directReports, people, userIdsWithMeetings]);

	return (
		<ProtectedContent permissions={Permission.CanGiveRecognition}>
			<VStack data-testid="team-tracker" px={rem(12)} pt={0} pb={4} gap={4} alignItems="stretch" position="relative">
				<ProgressBar people={people || []} />

				{/* MANAGER - DIRECT REPORTS WITH SECTIONS */}
				{hasDirectReports && <DirectReportList directReports={updatedDirectReports} />}

				{/* NON-MANAGER - TEAM TRACKER LIST */}
				{!hasDirectReports && people && <PersonList people={people} />}

				{/* Canvas for confetti animation */}
				<canvas
					id="progressBarWrapper"
					style={{
						position: 'absolute',
						width: '100%',
						height: '100%',
						top: 0,
						left: 0,
						pointerEvents: 'none',
					}}
				/>
			</VStack>
		</ProtectedContent>
	);
};
